/**
 * Calendário de dias úteis da Capacidade da Fábrica.
 *
 * Dia útil = segunda a sexta e fora do conjunto `naoUteis` (Set de
 * "AAAA-MM-DD" vindo de Configurações > Calendário).
 *
 * Toda a aritmética é feita sobre o NÚMERO DO DIA (dias desde 1970-01-01)
 * calculado com `Date.UTC`, que é conta pura e não depende do fuso. Nunca
 * `new Date("AAAA-MM-DD")`: essa forma é meia-noite UTC e, em São Paulo,
 * vira o dia ANTERIOR (ADR-0059). Os testes rodam com TZ=America/Sao_Paulo e
 * TZ=UTC para provar que o resultado não muda.
 *
 * Convenção de distância (plano, seção 4.1): início exclusivo, fim inclusivo.
 */

const MS_POR_DIA = 86400000;
const PADRAO_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const VAZIO = new Set();

// 1970-01-01 foi quinta-feira (4 na contagem domingo = 0).
const DIA_DA_SEMANA_DA_EPOCA = 4;
const SABADO = 6;
const DOMINGO = 0;
const SEGUNDA = 1;

/** Número do dia de um ISO válido; `null` para qualquer outra coisa. */
export function isoParaDia(iso) {
  if (typeof iso !== "string") return null;
  const partes = PADRAO_ISO.exec(iso);
  if (!partes) return null;
  const ano = Number(partes[1]);
  const mes = Number(partes[2]);
  const dia = Number(partes[3]);
  const ms = Date.UTC(ano, mes - 1, dia);
  const conferencia = new Date(ms);
  // Date.UTC aceita 31/02 e "corrige" para março: recusamos datas impossíveis.
  if (
    conferencia.getUTCFullYear() !== ano ||
    conferencia.getUTCMonth() !== mes - 1 ||
    conferencia.getUTCDate() !== dia
  ) {
    return null;
  }
  return Math.round(ms / MS_POR_DIA);
}

export function diaParaIso(numeroDoDia) {
  const data = new Date(numeroDoDia * MS_POR_DIA);
  const ano = data.getUTCFullYear();
  const mes = String(data.getUTCMonth() + 1).padStart(2, "0");
  const dia = String(data.getUTCDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

export function ehIsoValido(iso) {
  return isoParaDia(iso) !== null;
}

// Fail fast: data inválida no domínio é bug de quem chamou, não "sem data".
function exigirDia(iso, nome) {
  const dia = isoParaDia(iso);
  if (dia === null) {
    throw new TypeError(`${nome} precisa ser uma data AAAA-MM-DD válida (recebido: ${String(iso)}).`);
  }
  return dia;
}

function diaDaSemanaDoNumero(numeroDoDia) {
  return (((numeroDoDia + DIA_DA_SEMANA_DA_EPOCA) % 7) + 7) % 7;
}

function ehUtilPeloNumero(numeroDoDia, naoUteis) {
  const semana = diaDaSemanaDoNumero(numeroDoDia);
  if (semana === SABADO || semana === DOMINGO) return false;
  return !(naoUteis || VAZIO).has(diaParaIso(numeroDoDia));
}

/** 0 = domingo … 6 = sábado. */
export function diaDaSemana(iso) {
  return diaDaSemanaDoNumero(exigirDia(iso, "data"));
}

export function somarDiasCorridos(iso, dias) {
  return diaParaIso(exigirDia(iso, "data") + dias);
}

/** Diferença em dias corridos (`ate − de`). */
export function diasCorridosEntre(de, ate) {
  return exigirDia(ate, "ate") - exigirDia(de, "de");
}

export function ehDiaUtil(iso, naoUteis = VAZIO) {
  return ehUtilPeloNumero(exigirDia(iso, "data"), naoUteis);
}

/**
 * Dias úteis em (deExclusivo, ateInclusivo]. Com `ate` antes de `de` o
 * resultado é negativo (a mesma distância, no sentido contrário), para que
 * "quantos du faltam/passaram" seja uma conta só.
 */
export function duEntre(deExclusivo, ateInclusivo, naoUteis = VAZIO) {
  const de = exigirDia(deExclusivo, "deExclusivo");
  const ate = exigirDia(ateInclusivo, "ateInclusivo");
  if (ate === de) return 0;
  if (ate < de) return -duEntre(ateInclusivo, deExclusivo, naoUteis);
  let total = 0;
  for (let dia = de + 1; dia <= ate; dia += 1) {
    if (ehUtilPeloNumero(dia, naoUteis)) total += 1;
  }
  return total;
}

/** Lista dos dias úteis em [de, ate], em ordem. Vazio se `ate < de`. */
export function diasUteisDoIntervalo(deInclusivo, ateInclusivo, naoUteis = VAZIO) {
  const de = exigirDia(deInclusivo, "deInclusivo");
  const ate = exigirDia(ateInclusivo, "ateInclusivo");
  const dias = [];
  for (let dia = de; dia <= ate; dia += 1) {
    if (ehUtilPeloNumero(dia, naoUteis)) dias.push(diaParaIso(dia));
  }
  return dias;
}

/** Quantidade de dias úteis em [de, ate]. */
export function contarDiasUteis(deInclusivo, ateInclusivo, naoUteis = VAZIO) {
  if (exigirDia(ateInclusivo, "ateInclusivo") < exigirDia(deInclusivo, "deInclusivo")) return 0;
  return duEntre(somarDiasCorridos(deInclusivo, -1), ateInclusivo, naoUteis);
}

/**
 * Anda `n` dias úteis a partir de `iso` (que não conta). `n = 0` devolve o
 * próprio `iso`; `n < 0` anda para trás. Ex.: sexta + 1 du = segunda.
 */
export function somarDu(iso, n, naoUteis = VAZIO) {
  let dia = exigirDia(iso, "data");
  if (!Number.isInteger(n)) {
    throw new TypeError(`somarDu espera um número inteiro de dias úteis (recebido: ${String(n)}).`);
  }
  const passo = n >= 0 ? 1 : -1;
  let faltam = Math.abs(n);
  while (faltam > 0) {
    dia += passo;
    if (ehUtilPeloNumero(dia, naoUteis)) faltam -= 1;
  }
  return diaParaIso(dia);
}

/**
 * O k-ésimo dia útil contando `inicioInclusivo` como o primeiro, se ele for
 * útil (k ≥ 1). É a régua da fila: "hoje, se for útil, é o dia 1".
 */
export function diaUtilDeOrdem(inicioInclusivo, k, naoUteis = VAZIO) {
  if (!Number.isInteger(k) || k < 1) {
    throw new TypeError(`diaUtilDeOrdem espera k inteiro ≥ 1 (recebido: ${String(k)}).`);
  }
  return somarDu(somarDiasCorridos(inicioInclusivo, -1), k, naoUteis);
}

/** Segunda-feira da semana (seg a dom) que contém `iso`. */
export function inicioDaSemana(iso) {
  const dia = exigirDia(iso, "data");
  const semana = diaDaSemanaDoNumero(dia);
  const recuo = semana === DOMINGO ? 6 : semana - SEGUNDA;
  return diaParaIso(dia - recuo);
}

export function inicioDoMes(iso) {
  exigirDia(iso, "data");
  return `${iso.slice(0, 7)}-01`;
}

export function fimDoMes(iso) {
  exigirDia(iso, "data");
  const ano = Number(iso.slice(0, 4));
  const mes = Number(iso.slice(5, 7));
  // Dia 0 do mês seguinte = último dia deste mês (conta em UTC, sem fuso).
  return diaParaIso(Math.round(Date.UTC(ano, mes, 0) / MS_POR_DIA));
}

/** Soma meses ao 1º dia do mês de `iso` (o resultado é sempre dia 01). */
export function somarMeses(iso, meses) {
  exigirDia(iso, "data");
  const ano = Number(iso.slice(0, 4));
  const mes = Number(iso.slice(5, 7)) - 1 + meses;
  return diaParaIso(Math.round(Date.UTC(ano, mes, 1) / MS_POR_DIA));
}

export function menorIso(a, b) {
  return a <= b ? a : b;
}

export function maiorIso(a, b) {
  return a >= b ? a : b;
}

/** Anos tocados por [de, ate], em ordem. */
export function anosDoIntervalo(de, ate) {
  exigirDia(de, "de");
  exigirDia(ate, "ate");
  const anos = [];
  for (let ano = Number(de.slice(0, 4)); ano <= Number(ate.slice(0, 4)); ano += 1) anos.push(ano);
  return anos;
}

/**
 * "Hoje" no fuso do aparelho, pelos componentes LOCAIS do relógio (a régua
 * do usuário). `agora` é injetável para teste.
 */
export function hojeIso(agora = new Date()) {
  const ano = agora.getFullYear();
  const mes = String(agora.getMonth() + 1).padStart(2, "0");
  const dia = String(agora.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}
